import React, { useState } from 'react';
import Button3D from '../../ui/Button3D';
import Switch from '../../ui/Switch';
import { settingsProblem, settingsSubtitle } from '../eventSettings';
import { resolveEventWords } from '../eventWords';
import SettingsPopup, { Field } from './SettingsPopup';

const NOT_YET = 'Applies when the guest form adds these fields.';
const deadlineNotYet = (words) => `Applies when ${words.eventsLower} get an RSVP deadline.`;

function ToggleRow({ id, label, checked, onChange, note, disabled = false }) {
    return (
        <li className="ia-resv-field ia-resv-field--wide" data-testid={`toggle-${id}`}>
            <div className="flex items-center justify-between gap-3">
                <span id={`${id}-label`} className="text-sm font-bold">{label}</span>
                <Switch id={id} checked={checked} onChange={onChange} aria-label={label} describedBy={note ? `${id}-note` : undefined} disabled={disabled} />
            </div>
            {note && <p id={`${id}-note`} className="ia-resv-field__why">{note}</p>}
        </li>
    );
}

// Module 19 (BL-380) G8 - "Guests & RSVP": the RSVP form's defaults and the read-only explainer of how heads are counted.
// "Maybe" off hides it from the guest screen and import; guests already on a list keep their status.
export default function GuestsRsvpSettingsPopup({ settings, saving, error, onSave, onClose }) {
    const [form, setForm] = useState({
        ask_meal_choice: settings.ask_meal_choice,
        ask_dietary: settings.ask_dietary,
        allow_maybe: settings.allow_maybe,
        rsvp_deadline_days: String(settings.rsvp_deadline_days),
    });
    const [problem, setProblem] = useState('');
    const flip = (name) => (value) => setForm((previous) => ({ ...previous, [name]: value }));
    const set = (name) => (event) => setForm((previous) => ({ ...previous, [name]: event.target.value }));

    const submit = (event) => {
        event?.preventDefault?.();
        const next = { ...settings, ...form, rsvp_deadline_days: Number(form.rsvp_deadline_days) };
        const message = settingsProblem({ ...next, rsvp_deadline_days: form.rsvp_deadline_days === '' ? NaN : next.rsvp_deadline_days });
        if (message) return setProblem(message);
        setProblem('');
        onSave(next);
    };
    const shown = problem || error;
    const words = resolveEventWords(settings);

    return (
        <SettingsPopup
            title="Guests & RSVP"
            subtitle={settingsSubtitle(words)}
            size="wide"
            tileKey="guests-rsvp"
            testId="events-settings-guests"
            onClose={onClose}
            actions={(
                <>
                    <Button3D onClick={onClose} disabled={saving}>Cancel</Button3D>
                    <Button3D variant="go" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button3D>
                </>
            )}
        >
            <form onSubmit={submit} noValidate>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wide">RSVP form</h3>
                <ul className="ia-resv-grid" aria-label="RSVP form defaults">
                    <ToggleRow id="set-meal" label="Ask for meal choice" checked={form.ask_meal_choice} onChange={flip('ask_meal_choice')} note={NOT_YET} />
                    <ToggleRow id="set-diet" label="Ask about dietary needs and allergies" checked={form.ask_dietary} onChange={flip('ask_dietary')} note={NOT_YET} />
                    <ToggleRow id="set-maybe" label="Allow “Maybe”" checked={form.allow_maybe} onChange={flip('allow_maybe')} note="When off, Maybe is hidden from the guest screen and the import. Guests who are already Maybe keep it." />
                    <Field id="set-deadline" label={`RSVP deadline (days before the ${words.eventLower})`} hint={`Default 21. ${deadlineNotYet(words)}`}>
                        <input id="set-deadline" type="number" min="0" max="365" inputMode="numeric" value={form.rsvp_deadline_days} onChange={set('rsvp_deadline_days')} />
                    </Field>
                </ul>

                <h3 className="mb-2 mt-4 text-sm font-bold uppercase tracking-wide">How heads are counted</h3>
                <div className="ia-resv-field ia-resv-field--wide" data-testid="heads-explainer">
                    <p className="text-sm"><strong>Confirmed heads</strong> are guests who said Yes, plus their plus-ones: the number to give the caterer.</p>
                    <p className="mt-2 text-sm"><strong>Projected heads</strong> are everyone who has not said No (Yes, Maybe and No reply), plus-ones included: the number to plan for.</p>
                    <p className="ia-resv-field__why">This is how the Guests &amp; RSVP screen counts. It is not a setting.</p>
                </div>
                {shown && <p className="ia-resv-error" role="alert">{shown}</p>}
            </form>
        </SettingsPopup>
    );
}
